import React, {useCallback} from 'react';
import styled, {css} from 'styled-components';
import {Drop, DropType} from '../../../../grpc_client/grips/protos/shared/drop';
import {formatDateWithWrittenMonth} from '../../../../localization/date';
import {useDropsTranslationContext} from '../../../../pages/Drops/context/TranslationProvider';
import {DROP_PATH_TO_TYPE_MAP} from '../../../../pages/Drops/utils/constants';
import {cdnImageUrl} from '../../../../utils/cdnImageUrl';
import useDropClickHandler from '../../../../utils/hooks/useDropClick';
import useDropCountDownText from '../../../../utils/hooks/useDropCountDownText';
import useDropShareUrl from '../../../../utils/hooks/useDropShareUrl';
import useDropTypeFromPath from '../../../../utils/hooks/useDropTypeFromPath';
import useDropSubscribeClick from '../../../../utils/hooks/useDropsSubscribeClick';
import {useMedia} from '../../../../utils/hooks/useMedia';
import SimpleButton from '../../../Buttons/SimpleButton';
import Icon, {IconSize} from '../../../Icon/Icon';
import {GenericIconName} from '../../../Icon/IconName';
import {backgroundColor, primaryColor} from '../../../constants/colors';
import {mediaMinDesktop, mediaMinTablet} from '../../../constants/customMedia';
import {fontSize14, fwMediumBold, lhL} from '../../../constants/typography';
import {
  getTimelyLimitedDropReleasedUntilDate,
  isTimelyUnlimitedDrop,
} from '../../utils/helper';
import DropsPromotionTracking from '../TrackingWrapper/DropsPromotionTracking';
import {DropInfo, StyledHeadline} from '../shared/DropInfo';
import {DropTeaserArea} from '../shared/DropTeaserArea';

interface DropTeaserProps {
  drop: Drop;
  isMissedDrop?: boolean;
  isSubscribed?: boolean;
  subscribeCTA?: string;
  disableSharing?: boolean;
}

const Container = styled.section<{isMissedDrop: boolean}>`
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.04), 0 10px 15px 0 rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  overflow: hidden;
  width: 100%;

  @media ${mediaMinTablet} {
    width: calc(50% - 6px); // 12px inbetween
  }

  @media ${mediaMinDesktop} {
    width: calc(50% - 12px); // 24px inbetween
  }

  ${({isMissedDrop}) =>
    isMissedDrop &&
    css`
      filter: grayscale(100%);
    `}

  & + & {
    margin-top: 20px;

    @media ${mediaMinTablet} {
      margin-top: 0;
      &:nth-child(n + 3) {
        margin-top: 20px;
      }
    }
  }

  ${StyledHeadline} {
    margin-bottom: 0;
  }
`;

const StyledDropTeaserArea = styled(DropTeaserArea)`
  height: 375px;
  @media ${mediaMinDesktop} {
    height: 390px;
    background-position: center;
  }
`;

const StyledCTAButton = styled(SimpleButton)<{isSubscribed?: boolean}>`
  align-self: flex-start;
  width: auto;
  margin-top: 22px;
  width: 100%;
  border-radius: 2px;

  ${({isSubscribed}) =>
    isSubscribed &&
    css`
      background: ${backgroundColor};
      color: ${primaryColor};
      opacity: 1;
      filter: unset;
    `}

  @media ${mediaMinDesktop} {
    width: 241px;
    height: 48px;
  }
`;

const CTA = styled.span`
  ${fontSize14};
  ${fwMediumBold};
  line-height: ${lhL};
  margin-left: 4px;
  margin-top: -2px;
`;

const DropTeaser: React.FC<DropTeaserProps> = ({
  drop,
  isMissedDrop = false,
  isSubscribed,
  subscribeCTA,
  disableSharing,
}) => {
  const dropType = useDropTypeFromPath();
  const dropTypeMapped = DROP_PATH_TO_TYPE_MAP[dropType];
  const isPremiumDrop = dropTypeMapped === DropType.DROP_TYPE_AY_PREMIUM;
  const isExclusiveDrop = dropTypeMapped === DropType.DROP_TYPE_AY_LABEL;
  const onDropClick = useDropClickHandler(drop);
  const {
    exclusiveAtAboutYouShoesRelease,
    exclusiveAtAboutYouDrop,
    nowNew,
    overview: {
      passedDrop: {expired},
    },
    locale,
  } = useDropsTranslationContext();
  const isDesktop = useMedia(
    {
      [mediaMinDesktop]: true,
    },
    false,
  );
  const backgroundImageUrl = isDesktop
    ? cdnImageUrl(drop.desktopImage)
    : cdnImageUrl(drop?.mobileWebImage);

  const formattedDate = formatDateWithWrittenMonth(
    new Date(drop?.releasedFrom ?? Date.now()),
    locale,
  );
  const onDropSubscribeClick = useDropSubscribeClick({drop});

  const isTimelyUnlimited = isTimelyUnlimitedDrop(drop);
  const releasedUntil = getTimelyLimitedDropReleasedUntilDate(drop);

  const {
    countdownPillText: countdownTextWithTimer,
    timeLeft,
    isUpcomingDropClickable,
  } = useDropCountDownText(
    {
      releasedFrom: drop.releasedFrom,
      releasedUntil,
    },
    {
      isUpcomingDrop: !isMissedDrop,
    },
  );

  const getCountdownText = useCallback(() => {
    if (isMissedDrop) {
      if (isTimelyUnlimited) {
        return undefined;
      }

      return expired;
    }

    // if the drop's release is before 7 days from now show the data
    // otherwise show the countdown format
    if (timeLeft && timeLeft.days > 7) {
      return formattedDate;
    }

    return countdownTextWithTimer;
  }, [
    isMissedDrop,
    expired,
    timeLeft,
    formattedDate,
    countdownTextWithTimer,
    isTimelyUnlimited,
  ]);

  const shareUrl = useDropShareUrl(drop?.id);

  const getDropInfoSubline = (() => {
    if (isExclusiveDrop) {
      return exclusiveAtAboutYouDrop;
    }

    if (isPremiumDrop) {
      return nowNew;
    }

    return exclusiveAtAboutYouShoesRelease;
  })();

  return (
    <DropsPromotionTracking name={drop.informationText} id={`${drop.id}`}>
      {({ref, onClickCapture}) => (
        <Container
          {...((isMissedDrop || isUpcomingDropClickable) && {
            onClick: e => {
              e.preventDefault();
              e.stopPropagation();

              const clickOnTeaser =
                (e.target as Element).getAttribute('data-testid') !==
                  'overlay' &&
                !document.querySelector('[data-testid="overlay"]');

              if (clickOnTeaser) {
                onClickCapture();
                onDropClick();
              }
            },
          })}
          isMissedDrop={isMissedDrop}
          ref={ref}
        >
          <StyledDropTeaserArea
            backgroundImageUrl={backgroundImageUrl}
            countdownText={getCountdownText()}
            isPillStickyTop
            smallPill={isMissedDrop && !isDesktop}
            {...(!isMissedDrop && {usps: drop?.usps})}
            headline={drop?.headline}
            subline={drop?.subline}
            shareUrl={disableSharing ? '' : shareUrl}
          />
          <DropInfo
            headline={drop?.brandName}
            subline={getDropInfoSubline}
            showInformationText={true}
            {...(!isMissedDrop && {
              CTAButton: (
                <>
                  <StyledCTAButton
                    disableShadow
                    isSubscribed={isSubscribed}
                    onClick={onDropSubscribeClick}
                  >
                    <Icon
                      asset={
                        GenericIconName[
                          isSubscribed ? 'IcMailCheck' : 'IcMailWhite'
                        ]
                      }
                      size={IconSize.S}
                    />
                    <CTA>{subscribeCTA}</CTA>
                  </StyledCTAButton>
                </>
              ),
            })}
            informationText={drop?.informationText}
          />
        </Container>
      )}
    </DropsPromotionTracking>
  );
};

export default DropTeaser;
